import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
    selector: 'app-number-input',
    templateUrl: './number-input.component.html',
    styleUrls: ['../../styles.scss', './number-input.component.scss'],
    standalone: false
})
export class NumberInputComponent implements OnChanges, OnDestroy {
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label = '';
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() focus = false;
  @Input() placeholder = '';
  @Input() parentFormGroup: FormGroup = new FormGroup<any>({});
  @Input() controlName = '';
  @Input() error = '';
  @Input() maxLength: string | number = 20;
  @Input() isDecimalAllowed = false;
  @Input() decimalPlaces = 2;
  @Input() max = 9999999999;
  @Input() min = 0;
  @Input() prefixIcon = '';
  @Input() showCurrency = false;
  @Input() suffixIcon = '';
  @Input() suffixText = '';
  @Input() hint = '';
  @Input() warning = '';
  @Output() checkForErrors = new EventEmitter();

  matcher = new CustomErrorStateMatcher();

  fieldSubscription: Subscription | undefined;

  isRequired = false;
  isDisabled = false;

  constructor() {
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnChanges(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';
    this.fieldSubscription = control?.valueChanges.subscribe(() => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName]?.touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}

class CustomErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}
